import React, { useContext, useId, useState } from 'react';
import * as Sentry from '@sentry/browser';
import {
  Icon,
  IconButton,
  ImageUpload,
  RadioButton,
  RadioGroup,
  TextField,
  Toggle,
  useResponsiveValue,
} from '@monash/portal-react';

import DraggableWindow from '@/components/ui/draggable-window/DraggableWindow';
import {
  deepClone,
  useApi,
  postCustomImage,
  deleteCustomImage,
} from '@monash/portal-frontend-common';
import { formatUrl } from '@/utils/format-url';
import { WidgetContext } from '@/components/providers/WidgetProvider';
import { MOBILE_RESPONSIVE } from '@/components/ui/main/Main';
import IconInput from '@/components/pages/custom/components/icon-input/IconInput';
import ColourList from '@/components/pages/custom/widget-container/edit-menu/additional-options/colour-list/ColourList';
import { backgroundColorOptions } from '@/components/pages/custom/widget-container/edit-menu/additional-options/colorOptions';
import { IMAGE_EFFECTS } from '../../../../../../constants';
import { useSnackbar } from '@/components/providers/SnackbarProvider';
import c from './edit-link.module.scss';

const IMAGE_EFFECT_KEY = 'imageEffect';

const EditLink = ({
  widgetId,
  data,
  updateData,
  currentLink,
  visible,
  escapeFocusRef,
  imageUrl,
  setImageUrl,
  setImageEffect: setImageEffectLocal,
  imageEffect,
  isLoading,
  setIsLoading,
  isLegacyUrl,
}) => {
  const { STRETCH_TO_FIT, REPEAT, BLUR } = IMAGE_EFFECTS;
  const headingText = 'Edit link tile';
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const isMobile = responsiveSize === 'S';

  const { addSnackbar } = useSnackbar();

  // draggable window initial position
  const getTriggerRefPosition = () => {
    if (!escapeFocusRef?.current) return;
    const position = escapeFocusRef.current.getBoundingClientRect();
    return { x: position.right, y: position.bottom };
  };

  // Edit Link
  const [inEditLink, setInEditLink] = useState(currentLink || {});
  const { editActiveWidget, setEditActiveWidget } = useContext(WidgetContext);

  // deactivate widget from editing shortcuts in provider
  const deactivateWidget = () => setEditActiveWidget(null);

  const updateEditLink = (value, key) => {
    setInEditLink((prev) => {
      const newLink = deepClone(prev || {});
      newLink[key] = value;

      const formattedEditLink = {
        ...newLink,
        ...(newLink.url ? { url: formatUrl(newLink.url) } : {}),
      };
      updateData('link', [formattedEditLink]);

      return newLink;
    });
  };
  const [isUrlValid, setIsUrlValid] = useState(
    currentLink ? inEditLink.url : null
  );

  const toggleLinkName = () => {
    updateData('visible', !visible);
  };

  const textSizeOptions = [
    {
      value: 'Small',
      label: 'Select small font size',
      size: '1.6rem',
    },
    {
      value: 'Medium',
      label: 'Select medium font size',
      size: '2.4rem',
    },
    {
      value: 'Large',
      label: 'Select large font size',
      size: '3.6rem',
    },
  ];

  const { call: callPostCustomImage } = useApi({
    requestFn: postCustomImage,
    skipInitialCall: true,
  });

  const { call: callDeleteCustomImage } = useApi({
    requestFn: deleteCustomImage,
    skipInitialCall: true,
  });

  const uploadImage = (fileName, encodedValue) => {
    setIsLoading(true);
    callPostCustomImage(fileName, widgetId, encodedValue)
      .then(() => {
        setIsLoading(false);
        setFileName(fileName);
        setImageUrl(encodedValue);
      })
      .catch((error) => {
        const SERVER_ERROR_MSG =
          "We're not able to save your image right now - please try again later";
        const CLIENT_ERROR_MSG =
          'This file cannot be accepted - please use a different file';
        const isUserError = String(error.status).startsWith('4');
        const message = isUserError ? CLIENT_ERROR_MSG : SERVER_ERROR_MSG;

        setIsLoading(false);
        addSnackbar({
          message,
          type: 'error',
        });
        setIsLoading(false);
        Sentry.captureException(error);
      });
  };

  const deleteImage = () => {
    setIsLoading(true);

    if (isLegacyUrl) {
      updateData('backgroundURL', null);
      setImageUrl(null);
      setFileName(null);
      setIsLoading(false);
    } else {
      callDeleteCustomImage(widgetId)
        .then(() => {
          setImageUrl(null);
          setFileName(null);
          setIsLoading(false);

          if (imageEffect !== STRETCH_TO_FIT) {
            setImageEffect(STRETCH_TO_FIT);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          Sentry.captureException(error);
          addSnackbar({
            message:
              "We're not able to remove your image right now - please try again later",
            type: 'error',
          });
        });
    }
  };

  const setImageEffect = (effect) => {
    setImageEffectLocal(effect);
    updateData(IMAGE_EFFECT_KEY, effect);
  };

  const [fileName, setFileName] = useState(null);
  const [size, setSize] = useState(textSizeOptions[1]);
  const setTextSize = (currentSize) => {
    setSize(currentSize);
    updateData('fontSize', currentSize.size);
  };

  // File name inferred from imageUrl, mostly to support the lack
  // of file name from legacy backgroundURL
  const splitUrl = imageUrl?.split('/');
  const inferredFileName = splitUrl?.[splitUrl.length - 1];

  const imageEffectsHeading = useId();

  return (
    <DraggableWindow
      fullScreen={isMobile}
      title={headingText}
      isShowing={editActiveWidget}
      focusTrapEnabled={true}
      focusTrapSelectors={
        'button:not([disabled]),' +
        'input[type=text],' +
        'input[type=url],' +
        "[role='button']," + // for image input when it has no uploaded image
        'input[type=radio]:checked:not(:disabled)' // for image effect radio buttons
      }
      onClose={() => {
        deactivateWidget();
        escapeFocusRef.current?.focus();
      }}
      initialPosition={getTriggerRefPosition()}
      extraRootProps={{
        role: 'dialog',
        'aria-label': headingText,
      }}
      className={c.extraStyles}
    >
      <div className={c.editLink}>
        <div className={c.section}>
          <div className={c.item}>
            <label className={c.label}>Link name</label>
            <div className={c.linkNameContainer}>
              <TextField
                value={inEditLink?.name}
                type="text"
                placeholder="Link name"
                onChange={(e) => updateEditLink(e.target.value, 'name')}
              />

              <IconButton
                icon={visible ? Icon.Eye : Icon.EyeClosed}
                onClick={toggleLinkName}
              />
            </div>
          </div>
          <div className={c.item}>
            <label className={c.label}>Link URL</label>
            <TextField
              value={inEditLink?.url}
              type="url"
              placeholder="https://www.example.com/"
              error={isUrlValid !== null ? () => !isUrlValid : null}
              onChange={(e) => updateEditLink(e.target.value, 'url')}
              onBlur={(e) => {
                setIsUrlValid(inEditLink?.url);
                updateEditLink(e.target.value, 'url');
              }}
              onPaste={(e) => {
                e.preventDefault();
                const value = e.clipboardData.getData('Text');
                setIsUrlValid(value);
                updateEditLink(value, 'url', true);
              }}
              errorMsg="URL is required"
            />
          </div>
        </div>

        <div className={c.section}>
          <div className={c.item}>
            <label className={c.label}>Text size</label>
            <Toggle
              options={textSizeOptions}
              selected={size}
              setSelected={setTextSize}
              label="Change view type"
              mode="card"
              fullWidth={true}
            />
          </div>

          <div className={c.item}>
            <label className={c.label}>Text and background colour</label>
            <ColourList
              selectedColour={data.backgroundColor}
              onSelect={(colour) =>
                updateData(backgroundColorOptions.key, colour)
              }
              screenReaderPrefix={backgroundColorOptions.name}
              colours={backgroundColorOptions.options}
            />
          </div>
        </div>

        <div className={c.section}>
          <div className={c.item}>
            <label className={c.label}>Icon</label>
            <IconInput
              iconInput={inEditLink?.icon}
              setIconInput={(icon) => updateEditLink(icon, 'icon')}
              currentUrl={inEditLink?.url}
              urlInput={inEditLink?.url}
            />
          </div>
          <div className={c.item}>
            <ImageUpload
              onUpload={uploadImage}
              onDelete={deleteImage}
              fileName={data?.imageFileName || fileName || inferredFileName}
              imageURL={imageUrl}
              maxFileSize={7}
              isLoading={isLoading}
            />
          </div>

          <div className={c.item}>
            <div id={imageEffectsHeading} className={c.label}>
              Image effects
            </div>
            <RadioGroup aria-labelledby={imageEffectsHeading}>
              <RadioButton
                onClick={() => setImageEffect(STRETCH_TO_FIT)}
                on={imageEffect === STRETCH_TO_FIT}
                label="Stretch to fit"
                disabled={!imageUrl}
              />
              <RadioButton
                onClick={() => setImageEffect(REPEAT)}
                on={imageEffect === REPEAT}
                label="Repeat"
                disabled={!imageUrl}
              />
              <RadioButton
                onClick={() => setImageEffect(BLUR)}
                on={imageEffect === BLUR}
                label="Blur"
                disabled={!imageUrl}
              />
            </RadioGroup>
          </div>
        </div>
      </div>
    </DraggableWindow>
  );
};

export default EditLink;
